import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { paymentService } from 'api/services/paymentService';
import { bankAccountService } from 'api/services/bankAccountService';
import {
  isReceivingBankAccountRequired,
  isReceivingBankAccountVisible,
} from 'api/types';
import { usePaymentModes } from 'hooks/api/usePaymentModes';
import type { Payment, CreatePaymentRequest, UpdatePaymentRequest } from 'api/types';
import type { CollectionPoint } from 'api/types/payment.types';
import { STORAGE_KEYS } from 'auth/storageKeys';
import { configService } from 'api/services/configService';
import type { SelectOption } from 'components/ui/SyutSelect';

interface PaymentCaptureModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  /** Booking ID is required in create mode and pre-fills the payment row. */
  bookingId?: string;
  /** Property ID drives the receiving bank account dropdown's filtering. */
  propertyId?: string;
  /** Suggested amount — used to pre-fill the input in create mode (typically balance_due). */
  suggestedAmount?: number;
  /** Booking number — shown in the modal header for context. */
  bookingNumber?: string;
  record?: Payment | null;
  onSave: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', recordId?: string) => void;
  /** Where the receipt is taken — FRONT_OFFICE counts in the desk's daily closing (slice 7d);
   *  RESERVATIONS takes no cash (BR-FOC-012). */
  collectionPoint?: CollectionPoint;
}

/** advance-payments: record-accounts-receipt — "Recorded by Accounts" (BR-FOC-011). */
const canRecordForAccounts = (): boolean => {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.RBAC_MODULE_ACTIONS) || '{}');
    return Array.isArray(all?.['advance-payments']) && all['advance-payments'].includes('record-accounts-receipt');
  } catch { return false; }
};

interface PaymentFormData {
  amount: string;
  paymentMode: string;
  paymentDate: string;
  paymentReference: string;
  bankName: string;
  chequeNumber: string;
  receivingBankAccountId: string;
  remarks: string;
  /** "Recorded by Accounts" — the advance is already in Fairbooks (create mode only). */
  recordedByAccounts: boolean;
  acctTxnRef: string;
  bankValueDate: string;
}

/** Today on the desk's calendar (not UTC) — the receipt's date decides its Front Office day. */
const today = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const buildInitialFormData = (suggestedAmount?: number): PaymentFormData => ({
  amount: suggestedAmount && suggestedAmount > 0 ? suggestedAmount.toFixed(2) : '',
  paymentMode: '',
  paymentDate: today(),
  paymentReference: '',
  bankName: '',
  chequeNumber: '',
  receivingBankAccountId: '',
  remarks: '',
  recordedByAccounts: false,
  acctTxnRef: '',
  bankValueDate: '',
});

const PaymentCaptureModal: React.FC<PaymentCaptureModalProps> = ({
  isOpen, mode, bookingId, propertyId, suggestedAmount, bookingNumber,
  record, onSave, onClose, tabId, moduleName, onRestoreModal, collectionPoint,
}) => {
  const { minimize } = useModalMinimize();
  const [formData, setFormData] = useState<PaymentFormData>(buildInitialFormData(suggestedAmount));
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const effectiveRecordRef = useRef<Payment | null | undefined>(record);

  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    const data: PaymentFormData = mode === 'edit' && rec
      ? {
          amount: String(rec.amount ?? ''),
          paymentMode: rec.paymentMode || '',
          paymentDate: rec.paymentDate ? rec.paymentDate.split('T')[0] : today(),
          paymentReference: rec.paymentReference || '',
          bankName: rec.bankName || '',
          chequeNumber: rec.chequeNumber || '',
          receivingBankAccountId: rec.receivingBankAccountId || '',
          remarks: rec.remarks || '',
          // Origin is fixed once recorded; edit mode only displays it.
          recordedByAccounts: rec.origin === 'FAIRBOOKS',
          acctTxnRef: rec.acctTxnRef || '',
          bankValueDate: rec.bankValueDate ? rec.bankValueDate.split('T')[0] : '',
        }
      : buildInitialFormData(suggestedAmount);

    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentRecord = recordRef.current;
    let modalId: string;
    let title: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-payment-${bookingId || 'default'}`;
      title = bookingNumber
        ? `Add Advance - ${bookingNumber}${currentFormData.amount ? ` (₹${currentFormData.amount})` : ''}`
        : 'Add Advance';
    } else {
      modalId = `${tabId}-form-modal-edit-payment-${currentRecord?.paymentId || 'new'}`;
      title = `Edit Payment - ${currentRecord?.bookingNumber || ''}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(currentMode, currentMode === 'edit' ? currentRecord?.paymentId : undefined);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, bookingId, bookingNumber]);

  const loadBankAccountOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    if (!propertyId) return [];
    const res = await bankAccountService.getForBooking(propertyId);
    const items = res.data || [];
    const filtered = input
      ? items.filter(o => `${o.accountCode} ${o.accountName} ${o.bankName}`.toLowerCase().includes(input.toLowerCase()))
      : items;
    return filtered.map(o => ({
      value: o.id,
      label: `${o.accountCode} – ${o.accountName} (${o.bankName})${o.isShared ? ' [Shared]' : o.isPrimary ? ' [Primary]' : ''}`,
    }));
  }, [propertyId]);

  const showBankAccount = useMemo(() => isReceivingBankAccountVisible(formData.paymentMode), [formData.paymentMode]);
  const requireBankAccount = useMemo(() => isReceivingBankAccountRequired(formData.paymentMode), [formData.paymentMode]);
  const showCheque = formData.paymentMode === 'Cheque';
  const showPayerBank = formData.paymentMode === 'Cheque' || formData.paymentMode === 'NEFT/RTGS';

  // Property-scoped enabled payment modes (PROPERTY override → SYSTEM default).
  // Refetched on modal open + restore via useEffect below so changes by another
  // admin in a separate tab are picked up before the cashier acts.
  const { modes: enabledModes, refetch: refetchPaymentModes } = usePaymentModes(propertyId);

  // Build the dropdown options. In edit mode, if the persisted mode is no longer
  // enabled, surface it as a "(disabled)" option so the row stays editable.
  // The property's cash modes (FRONT_OFFICE_CLOSING: fo.cash_payment_modes) — Reservations holds no cash.
  const [cashModes, setCashModes] = useState<string[]>(['Cash']);
  useEffect(() => {
    if (!isOpen || !propertyId) return;
    let live = true;
    configService.getEffectiveConfig('FRONT_OFFICE_CLOSING', [{ type: 'PROPERTY', id: propertyId }])
      .then((res) => {
        const v = res.data?.find((c) => c.configKey === 'fo.cash_payment_modes');
        const list = (v?.effectiveValue ?? v?.defaultValue ?? 'Cash').split(',').map((s) => s.trim()).filter(Boolean);
        if (live) setCashModes(list.length ? list : ['Cash']);
      })
      .catch(() => { /* keep the default */ });
    return () => { live = false; };
  }, [isOpen, propertyId]);
  const showAccountsTick = mode === 'create' && collectionPoint !== 'FRONT_OFFICE' && canRecordForAccounts();
  const noCash = collectionPoint === 'RESERVATIONS' && !formData.recordedByAccounts;
  const isCashMode = useCallback((m: string) => cashModes.some((c) => c.toLowerCase() === m.trim().toLowerCase()), [cashModes]);

  const paymentModeOptions = useMemo<SelectOption[]>(() => {
    const opts: SelectOption[] = enabledModes
      .filter(m => !(noCash && mode === 'create' && isCashMode(m)))
      .map(m => ({ value: m, label: m }));
    if (mode === 'edit' && formData.paymentMode && !enabledModes.includes(formData.paymentMode)) {
      opts.unshift({ value: formData.paymentMode, label: `${formData.paymentMode} (disabled)` });
    }
    return opts;
  }, [enabledModes, mode, formData.paymentMode, noCash, isCashMode]);

  // A cash mode picked before Reservations' rule applied (or before "Recorded by Accounts" was untick'd) is cleared.
  useEffect(() => {
    if (mode === 'create' && noCash && formData.paymentMode && isCashMode(formData.paymentMode))
      setFormData(prev => ({ ...prev, paymentMode: '' }));
  }, [mode, noCash, formData.paymentMode, isCashMode]);

  // Refetch enabled modes whenever the modal becomes visible (including restore
  // from minimized state) — config can change between cashier sessions.
  useEffect(() => {
    if (isOpen) refetchPaymentModes(propertyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, propertyId]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) errors.amount = 'Amount must be greater than zero';
    if (!formData.paymentMode) errors.paymentMode = 'Payment mode is required';
    if (!formData.paymentDate) errors.paymentDate = 'Payment date is required';

    if (formData.paymentMode && formData.paymentMode !== 'Cash' && !formData.paymentReference.trim()) {
      errors.paymentReference = `Reference is required for ${formData.paymentMode}`;
    }
    if (formData.paymentMode === 'Cheque' && !formData.chequeNumber.trim()) {
      errors.chequeNumber = 'Cheque number is required';
    }
    if (requireBankAccount && !formData.receivingBankAccountId) {
      errors.receivingBankAccountId = `Receiving bank account is required for ${formData.paymentMode}`;
    }
    // The Fairbooks reference is the only thing preventing a second receipt in Fairbooks.
    if (mode === 'create' && formData.recordedByAccounts && !formData.acctTxnRef.trim()) {
      errors.acctTxnRef = 'Enter the Fairbooks voucher reference Accounts gave you';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, requireBankAccount, mode]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        const payload: UpdatePaymentRequest = {
          amount: parseFloat(formData.amount),
          paymentMode: formData.paymentMode,
          paymentDate: formData.paymentDate,
          paymentReference: formData.paymentReference.trim() || undefined,
          bankName: formData.bankName.trim() || undefined,
          chequeNumber: formData.chequeNumber.trim() || undefined,
          receivingBankAccountId: formData.receivingBankAccountId || undefined,
          remarks: formData.remarks.trim() || undefined,
          rowVersion: eff.rowVersion,
        };
        await paymentService.update(eff.paymentId, payload);
      } else {
        if (!bookingId) throw new Error('Booking is required to record a payment');
        const payload: CreatePaymentRequest = {
          bookingId,
          amount: parseFloat(formData.amount),
          paymentMode: formData.paymentMode,
          paymentDate: formData.paymentDate,
          paymentReference: formData.paymentReference.trim() || undefined,
          bankName: formData.bankName.trim() || undefined,
          chequeNumber: formData.chequeNumber.trim() || undefined,
          receivingBankAccountId: formData.receivingBankAccountId || undefined,
          paymentType: 'Advance',
          remarks: formData.remarks.trim() || undefined,
          collectionPoint: formData.recordedByAccounts ? 'ACCOUNTS' : collectionPoint,
          ...(formData.recordedByAccounts
            ? {
                origin: 'FAIRBOOKS' as const,
                acctTxnRef: formData.acctTxnRef.trim(),
                bankValueDate: formData.bankValueDate || undefined,
              }
            : {}),
        };
        await paymentService.create(payload);
      }
      setIsSaving(false);
      notificationService.toast.success(
        mode === 'edit' ? 'Payment updated successfully.' : `Advance of ₹${formData.amount} recorded successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.message || err?.response?.data?.errors?.[0] || err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, bookingId, onSave]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto' }}>
        {isSaving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{mode === 'edit' ? 'Saving...' : 'Recording payment...'}</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'edit' ? 'Edit Payment' : 'Record Advance Payment'}
            {bookingNumber && <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>· {bookingNumber}</span>}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-3">
            <div className="form-group" data-field="amount">
              <label className="form-label">Amount (₹) <span className="text-danger">*</span></label>
              <input
                type="number"
                className={`form-input${getFieldError('amount') ? ' form-field-error' : ''}`}
                value={formData.amount}
                onChange={(e) => { clearFieldError('amount'); setFormData(prev => ({ ...prev, amount: e.target.value })); }}
                min="0.01"
                step="0.01"
                autoFocus
              />
              {getFieldError('amount') && <div className="form-error-message">{getFieldError('amount')}</div>}
            </div>

            <div className="form-group" data-field="paymentMode">
              <SyutSelect
                label={<>Payment Mode <span className="text-danger">*</span></>}
                options={paymentModeOptions}
                value={formData.paymentMode}
                onChange={(val) => {
                  clearFieldError('paymentMode');
                  const newMode = (val as string) || '';
                  setFormData(prev => ({
                    ...prev,
                    paymentMode: newMode,
                    // Clear conditional fields when mode changes to keep payload clean.
                    bankName: (newMode === 'Cheque' || newMode === 'NEFT/RTGS') ? prev.bankName : '',
                    chequeNumber: newMode === 'Cheque' ? prev.chequeNumber : '',
                    receivingBankAccountId: isReceivingBankAccountVisible(newMode) ? prev.receivingBankAccountId : '',
                  }));
                }}
                isClearable={false}
                size="md"
                error={getFieldError('paymentMode')}
              />
            </div>

            <div className="form-group" data-field="paymentDate">
              <label className="form-label">Payment Date <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${getFieldError('paymentDate') ? ' form-field-error' : ''}`}
                value={formData.paymentDate}
                onChange={(e) => { clearFieldError('paymentDate'); setFormData(prev => ({ ...prev, paymentDate: e.target.value })); }}
              />
              {getFieldError('paymentDate') && <div className="form-error-message">{getFieldError('paymentDate')}</div>}
            </div>
          </div>

          {formData.paymentMode && formData.paymentMode !== 'Cash' && (
            <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
              <div className="form-group" data-field="paymentReference">
                <label className="form-label">Reference / Transaction ID <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-input${getFieldError('paymentReference') ? ' form-field-error' : ''}`}
                  value={formData.paymentReference}
                  onChange={(e) => { clearFieldError('paymentReference'); setFormData(prev => ({ ...prev, paymentReference: e.target.value })); }}
                  placeholder={formData.paymentMode === 'UPI' ? 'e.g., UTR123456' : formData.paymentMode === 'Cheque' ? 'e.g., bank ref no' : 'Transaction reference'}
                  maxLength={100}
                />
                {getFieldError('paymentReference') && <div className="form-error-message">{getFieldError('paymentReference')}</div>}
              </div>
              {showPayerBank && (
                <div className="form-group" data-field="bankName">
                  <label className="form-label">Payer's Bank{formData.paymentMode === 'Cheque' ? ' (cheque issuer)' : ''}</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.bankName}
                    onChange={(e) => setFormData(prev => ({ ...prev, bankName: e.target.value }))}
                    maxLength={100}
                  />
                </div>
              )}
            </div>
          )}

          {showCheque && (
            <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
              <div className="form-group" data-field="chequeNumber">
                <label className="form-label">Cheque Number <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className={`form-input${getFieldError('chequeNumber') ? ' form-field-error' : ''}`}
                  value={formData.chequeNumber}
                  onChange={(e) => { clearFieldError('chequeNumber'); setFormData(prev => ({ ...prev, chequeNumber: e.target.value })); }}
                  maxLength={50}
                />
                {getFieldError('chequeNumber') && <div className="form-error-message">{getFieldError('chequeNumber')}</div>}
              </div>
            </div>
          )}

          {showBankAccount && (
            <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
              <div className="form-group" data-field="receivingBankAccountId">
                <SyutSelect
                  label={
                    <>
                      Receiving Bank Account
                      {requireBankAccount && <span className="text-danger"> *</span>}
                      {!requireBankAccount && <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal', fontSize: '0.75rem', marginLeft: '0.5rem' }}>(fill when deposited)</span>}
                    </>
                  }
                  isAsync
                  loadOptions={loadBankAccountOptions}
                  defaultOptions={true}
                  cacheOptions={false}
                  value={formData.receivingBankAccountId}
                  onChange={(val) => { clearFieldError('receivingBankAccountId'); setFormData(prev => ({ ...prev, receivingBankAccountId: (val as string) || '' })); }}
                  placeholder={!propertyId ? 'Property required to load accounts' : 'Select receiving bank account...'}
                  isClearable={!requireBankAccount}
                  isDisabled={!propertyId}
                  size="md"
                  error={getFieldError('receivingBankAccountId')}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          )}

          {noCash && mode === 'create' && (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              Cash is not taken at Reservations — the guest pays cash at the Front Office desk.
            </div>
          )}
          {showAccountsTick && (
            <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.recordedByAccounts}
                  onChange={(e) => {
                    const on = e.target.checked;
                    clearFieldError('acctTxnRef');
                    setFormData(prev => ({ ...prev, recordedByAccounts: on, acctTxnRef: on ? prev.acctTxnRef : '', bankValueDate: on ? prev.bankValueDate : '' }));
                  }}
                  style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
                />
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>Recorded by Accounts</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>— already entered in Fairbooks; it will not be posted again</span>
              </label>
              {formData.recordedByAccounts && (
                <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
                  <div className="form-group" data-field="acctTxnRef">
                    <label className="form-label">Fairbooks Voucher Ref <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className={`form-input${getFieldError('acctTxnRef') ? ' form-field-error' : ''}`}
                      value={formData.acctTxnRef}
                      onChange={(e) => { clearFieldError('acctTxnRef'); setFormData(prev => ({ ...prev, acctTxnRef: e.target.value })); }}
                      placeholder="e.g., RV-000123"
                      maxLength={100}
                    />
                    {getFieldError('acctTxnRef') && <div className="form-error-message">{getFieldError('acctTxnRef')}</div>}
                  </div>
                  <div className="form-group" data-field="bankValueDate">
                    <label className="form-label">Bank Value Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.bankValueDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, bankValueDate: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {mode === 'edit' && formData.recordedByAccounts && (
            <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: '0.375rem', background: 'var(--bg-secondary)', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Recorded by Accounts — Fairbooks ref <strong style={{ fontFamily: 'monospace' }}>{formData.acctTxnRef || '-'}</strong>.
              A change here is not sent to Fairbooks; ask Accounts to correct the voucher there too.
            </div>
          )}

          <div style={{ marginTop: '0.5rem' }}>
            <div className="form-group" data-field="remarks">
              <label className="form-label">Remarks</label>
              <textarea
                className="form-input form-textarea"
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                rows={2}
                maxLength={500}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Record Payment'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Record Advance Payment'}
        message={
          mode === 'edit'
            ? `Update this payment to ₹${formData.amount} via ${formData.paymentMode}?`
            : formData.recordedByAccounts
              ? `Record an advance of ₹${formData.amount} via ${formData.paymentMode}, already entered in Fairbooks as ${formData.acctTxnRef.trim()}? It updates the booking's advance and balance, and is not posted to Fairbooks again.`
              : `Record an advance of ₹${formData.amount} via ${formData.paymentMode}? This will update the booking's advance received and balance due.`
        }
        confirmLabel={mode === 'edit' ? 'Save' : 'Record Payment'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Payment Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="OK"
        cancelLabel="Dismiss"
        variant="default"
        onConfirm={() => { setConcurrencyConflict({ open: false, message: '' }); onClose(); onSave(); }}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default PaymentCaptureModal;
